import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useOrderRepairBudget } from '@/hooks/useOrder';
import { useOrderMutations } from '@/hooks/useOrderMutations';
import RepairBudgetFormComponent from './RepairBudgetFormComponent';

const RepairBudgetFormContainer = ({ clientPanel }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  
  const [data, setData] = useState(null);
  const [roomsWithDamages, setRoomsWithDamages] = useState({});
  const [currentTag, setCurrentTag] = useState('');
  const [filterTag, setFilterTag] = useState([]);
  const [options, setOptions] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: remoteData, isLoading } = useOrderRepairBudget(orderId);
  const { updateOrderRepairBudget } = useOrderMutations(orderId);

  const optionsTag = useMemo(() => [
    {
      value: '',
      label: intl.formatMessage({ id: 'AllItems' }),
    },
    {
      value: 'TenantPending',
      label: intl.formatMessage({ id: 'TenantPending' }),
    },
    {
      value: 'OwnerPending',
      label: intl.formatMessage({ id: 'OwnerPending' }),
    },
  ], [intl]);

  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
      
      const newOptions = [];
      const newRoomsWithDamages = {};

      if (remoteData?.damages?.length) {
        newOptions.push({
          value: -1,
          label: intl.formatMessage({ id: 'GENERAL' }),
        });
      }

      remoteData?.rooms?.forEach((room, index) => {
        let hasDamages = room?.damages?.length > 0;
        if (!hasDamages) {
          hasDamages = room?.items?.some(item => item.damages?.length > 0);
        }

        if (hasDamages) {
          newRoomsWithDamages[index] = room;
          newOptions.push({
            value: index,
            label: `${+index + 1}. ${room.name}`,
          });
        }
      });

      setRoomsWithDamages(newRoomsWithDamages);
      setOptions(newOptions);
    }
  }, [remoteData, intl]);

  const handleInputChange = evt => {
    const { name: fullName, value } = evt.target;
    const [name, roomIndex, itemIndex, damageIndex] = fullName.split('-');

    if (itemIndex !== 'G') {
      setRoomsWithDamages(oldData => {
        const newData = { ...oldData };
        if (itemIndex === 'R') {
          newData[roomIndex].damages[damageIndex].repairBudget = {
            ...newData[roomIndex].damages[damageIndex].repairBudget,
            [name]: value,
          };
        } else {
          newData[roomIndex].items[itemIndex].damages[damageIndex].repairBudget = {
            ...newData[roomIndex].items[itemIndex].damages[damageIndex].repairBudget,
            [name]: value,
          };
        }
        return newData;
      });
    } else {
      setData(oldData => {
        const newData = { ...oldData };
        newData.damages[damageIndex].repairBudget = {
          ...newData.damages[damageIndex].repairBudget,
          [name]: value,
        };
        return newData;
      });
    }
  };

  const handleRoomChange = option => {
    setCurrentRoom(option);
  };

  const handleTagChange = option => {
    setCurrentTag(option.value);
    if (!option?.value) {
      setFilterTag([]);
    } else {
      if (option?.value === 'TenantPending') {
        setFilterTag(['Pendência do Inquilino']);
      } else if (option?.value === 'OwnerPending') {
        setFilterTag(['Pendência do Proprietário', 'Igual entrada + Pendência do Proprietário']);
      }
    }
  };

  const cancel = () => {
    navigate(`/orders/${orderId}`);
  };

  const onSubmit = async () => {
    const rooms = {
      ...data.rooms,
      ...roomsWithDamages,
    };

    const mergedData = {
      ...data,
      rooms: Object.keys(rooms).map(key => rooms[key]),
    };

    try {
      setBusy(true);
      await updateOrderRepairBudget.mutateAsync(mergedData);
      navigate(`/orders/${orderId}`);
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <RepairBudgetFormComponent
      busy={busy}
      cancel={cancel}
      clientPanel={clientPanel}
      currentRoom={currentRoom}
      currentTag={currentTag}
      filterTag={filterTag}
      data={data}
      handleInputChange={handleInputChange}
      handleRoomChange={handleRoomChange}
      handleTagChange={handleTagChange}
      isLoading={isLoading}
      onSubmit={onSubmit}
      options={options}
      optionsTag={optionsTag}
      roomsWithDamages={roomsWithDamages}
    />
  );
};

RepairBudgetFormContainer.propTypes = {
  clientPanel: PropTypes.bool.isRequired,
};

export default RepairBudgetFormContainer;
